import { Injectable, OnDestroy } from '@angular/core';
import * as dayjs from 'dayjs';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { LogService } from '../baseServices/logging/log.service';
import { ReviewPeriod } from '../entities/ReviewPeriod';
import { DateTimeHelper } from '../utils/date-time-helper';
import { TimeoutSchedulerService } from './timeout-scheduler.service';

/**
 * Provides the current review period (previous Saturday through next Saturday).
 * - Exposes `currentReviewPeriod$` (BehaviorSubject) so all consumers stay in sync.
 * - Automatically schedules an update at the exact next review period transition.
 * - Re-checks and updates when the scheduled timeout fires.
 */
@Injectable({
  providedIn: 'root'
})
export class CurrentReviewPeriodService implements OnDestroy {
  private static readonly TIMEOUT_ID = 'review-period-rollover';
  private readonly currentReviewPeriodSubject = new BehaviorSubject<ReviewPeriod>(ReviewPeriod.getCurrentReviewPeriod());
  public readonly currentReviewPeriod$ = this.currentReviewPeriodSubject.asObservable();
  private nextTransitionTime?: dayjs.Dayjs;
  private readonly destroy$ = new Subject<void>();
  
  constructor(
    private logService: LogService,
    private timeoutScheduler: TimeoutSchedulerService
  ) {
    this.logService.debug('CurrentReviewPeriodService.Initialized', this.logContext());
    this.loadSubscriptions();
    this.scheduleNextTimeout();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public get value(): ReviewPeriod {
    return this.currentReviewPeriodSubject.value;
  }

  private logContext() {
    return { startDate: DateTimeHelper.formatDateTime(this.value?.startDate), endDate: DateTimeHelper.formatDateTime(this.value?.endDate), nextTransition: this.nextTransitionTime ? DateTimeHelper.formatDateTime(this.nextTransitionTime) : null };
  }

  private loadSubscriptions() {
    this.timeoutScheduler.getTimeoutById(CurrentReviewPeriodService.TIMEOUT_ID)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.checkAndUpdate());
  }

  /**
   * Schedules an update for the exact next rollover of the review period.
   */
  private scheduleNextTimeout() {
    const currentPeriod = this.currentReviewPeriodSubject.value;
    this.nextTransitionTime = currentPeriod.getNextPeriodTransitionTime();
    const at = this.nextTransitionTime.toDate();
    this.timeoutScheduler.schedule({
      id: CurrentReviewPeriodService.TIMEOUT_ID,
      at,
      context: this.logContext()
    });
    this.logService.debug('CurrentReviewPeriodService.NextUpdateScheduled', this.logContext());
  }

  /**
   * Ensures currentReviewPeriod is correct — run on app resume and on timeout.
   */
  private checkAndUpdate = () => {
    const period = ReviewPeriod.getCurrentReviewPeriod();
    if (!DateTimeHelper.checkOnSameDay(period.startDate, this.currentReviewPeriodSubject.value?.startDate)
      || !DateTimeHelper.checkOnSameDay(period.endDate, this.currentReviewPeriodSubject.value?.endDate)
    ) {
      this.setCurrentReviewPeriod(period);
    }
    this.scheduleNextTimeout();
  };

  private setCurrentReviewPeriod(period: ReviewPeriod) {
    this.currentReviewPeriodSubject.next(period);
    this.logService.info('CurrentReviewPeriodService.ValueUpdated', this.logContext());
  }
}
